import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Typography,
  Autocomplete,
  TextField,
  Button,
  ButtonBase,
  Box,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  LinearProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { Field } from "formik";
import { DatePicker } from "antd";
import moment from "moment";
import dayjs from "dayjs";
import { genderOptions, stateOptions, titleOptions } from "../constants/formOptions";
import { titleAfterGenderChange, suggestTitle } from "../utils/proposerTitle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ToastError, ToastSuccess } from "../../../../UiComponents/Toaster/Toast";
import { GetFinancier } from "../../../../Service/_index";
import {
  extractAadharData,
  extractAadharDataFromBothSides,
  isValidAadharNumber,
  hasAnyAddress,
  PdfPasswordRequiredError,
} from "../utils/aadharOcr";
import { mergeImages, AI_MAX_SIDE } from "../utils/mergeImages";
import { isPdfFile, pdfFileToImageFile } from "../utils/pdfToImages";
import { upperCaseChange } from "../utils/inputHelpers";
import { NAVY, FONT } from "./formStyles";
import FormSection from "./FormSection";
import ExistingTpPolicySection from "./ExistingTpPolicySection";
import { requiresSeparateAadhar, hasPanCard, hasCompanyLeg, isKshemaOnlyPolicy, KSHEMA_FINANCIER_NAME_MIN_LETTERS } from "../utils/companyRules";

// one document tile's place in the Documents row: its own width on a laptop, the full width on a phone
const DOC_SLOT_SX = { width: { xs: "100%", sm: "auto" } };
// a group inside a section card ("Financier Details" in Vehicle details), with the rule above it
const SUBHEAD_SX = { fontFamily: FONT, fontSize: "14px", fontWeight: 700, color: NAVY, lineHeight: "20px", borderTop: "1px solid #EAECF0", pt: 2, mt: 1, mb: 1.5 };

/**
 * Customer, Address & Vehicle Details Step Component
 * Third and final step of the online policy form
 */
/**
 * A document that is already saved on the policy (came back from the API as
 * { fileName, key, location, presignedUrl }) rather than a freshly picked File.
 */
const isStoredDocument = (doc) =>
  !!doc && typeof doc === "object" && !(doc instanceof File);

/** Viewable URL for a stored document — presigned first, then raw location. */
const getStoredDocumentUrl = (doc) => {
  if (!doc) return null;
  if (typeof doc === "string") return doc;
  if (typeof doc === "object") return doc.presignedUrl || doc.location || null;
  return null;
};


/**
 * The Aadhaar number, read off the upload by OCR.
 *
 * A full-width RESULT BAR under the uploads, not a card beside them. As a card
 * it sat in the document row as if it were a third document you could upload,
 * competing with the tiles for attention and leaving a large empty box around
 * twelve digits. It is not a document — it is the readout of one, so it reads
 * across the bottom of the row it came from.
 *
 * Hidden until there is an upload: with nothing to read from there is no
 * result to report, and the upload tile beside it already says what to do.
 *
 * There is no input to type into, so this bar IS the error surface — when the
 * scan is too poor to yield 12 digits the only fix is a better image, and this
 * is where the user has to be told that.
 */
const AadharNumberStrip = ({ number, extracting, hasUpload }) => {
  if (!extracting && !hasUpload) return null;

  const ok = isValidAadharNumber(number);
  const tone = extracting
    ? { accent: NAVY, bg: "#F5F8FC", border: "#C9D7E8" }
    : ok
      ? { accent: "#067647", bg: "#F6FEF9", border: "#ABEFC6" }
      : { accent: "#B42318", bg: "#FFFBFA", border: "#FECDCA" };

  // Grouped 4-4-4, the way it is printed on the card. A 12-digit run is
  // genuinely hard to check against the card in your hand.
  const grouped = ok
    ? String(number).replace(/(\d{4})(\d{4})(\d{4})/, "$1 $2 $3")
    : "";

  // One slim line (2026-10-23, the owner: "not big big thing ... simple"): label, number, what to do. While the card is
  // read it is also the progress (a thin bar along its bottom) — there used to be a second, separate banner for that.
  return (
    <Box
      data-testid="aadhaar-number-strip"
      sx={{
        mt: 1.5,
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        columnGap: 1.25,
        rowGap: 0.25,
        flexWrap: "wrap",
        px: 1.5,
        py: 0.9,
        borderRadius: "8px",
        border: `1px solid ${tone.border}`,
        backgroundColor: tone.bg,
        transition: "background-color .15s, border-color .15s",
      }}
    >
      {extracting ? (
        <CircularProgress size={16} sx={{ color: tone.accent }} />
      ) : ok ? (
        <CheckCircleOutlineIcon sx={{ fontSize: 18, color: tone.accent }} />
      ) : (
        <ErrorOutlineIcon sx={{ fontSize: 18, color: tone.accent }} />
      )}

      <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: "#344054", whiteSpace: "nowrap" }}>
        Aadhaar number <span style={{ color: "#B42318" }}>*</span>
      </Typography>

      {extracting ? (
        <Skeleton variant="text" animation="wave" width={150} sx={{ fontSize: "15px" }} />
      ) : (
        <Typography
          sx={{
            fontSize: ok ? "15px" : "13px",
            fontWeight: 700,
            letterSpacing: ok ? "1.5px" : 0,
            color: ok ? NAVY : tone.accent,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }}
        >
          {ok ? grouped : "Could not read the number"}
        </Typography>
      )}

      <Typography
        sx={{
          fontSize: "12px",
          lineHeight: 1.45,
          color: extracting || ok ? "#667085" : tone.accent,
          ml: { xs: 0, sm: "auto" },
          flexBasis: { xs: "100%", sm: "auto" },
        }}
      >
        {extracting
          ? "Reading the Aadhaar card — the fields below fill in automatically."
          : ok
            ? "Read from the uploaded card. Please check it matches."
            : "Upload a sharper image — all 12 digits visible, no glare or cropping."}
      </Typography>

      {/* Indeterminate on purpose: the reading reports no progress, and a fake percentage that sticks reads as a hang. */}
      {extracting && (
        <LinearProgress
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: 2,
            backgroundColor: "transparent",
            "& .MuiLinearProgress-bar": { backgroundColor: NAVY },
          }}
        />
      )}
    </Box>
  );
};

/**
 * One document upload, as a single compact row:
 *
 *   [icon] Title *  [ON FILE]   filename.jpg        [Preview] [Replace] [Remove]
 *
 * It used to be a tall card with a 132px thumbnail inside a drop box, three
 * across — four documents took a whole screen. The picture is now behind a
 * Preview button (an image opens in a dialog, a PDF in a new tab), so the row
 * stays one line high whatever the file is.
 *
 * The whole row is still a drop target, so drag-and-drop keeps working.
 * Handles a freshly picked File and an already-saved document in the same
 * shell; they used to be two components rendering near-identical markup.
 */
const UploadCard = ({
  title,
  required = false,
  file,
  preview,
  storedDoc = null,
  disabled = false,
  onSelect,
  onRemove,
  variant = "row",
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const tileInputRef = useRef(null);

  const hasNewFile = !!file && !isStoredDocument(file);
  const hasStored = isStoredDocument(storedDoc);
  const state = hasNewFile ? "filled" : hasStored ? "saved" : "empty";
  const storedUrl = hasStored ? getStoredDocumentUrl(storedDoc) : null;

  const storedIsPdf =
    !!storedUrl &&
    ((storedDoc?.fileName || "").toLowerCase().endsWith(".pdf") ||
      String(storedUrl).toLowerCase().includes(".pdf"));

  // A PDF has no image to render in a dialog, so it opens in its own tab.
  const imgSrc = hasNewFile
    ? preview && preview !== "pdf"
      ? preview
      : null
    : storedIsPdf
      ? null
      : storedUrl;
  const isPdf =
    (hasNewFile && preview === "pdf") || (!hasNewFile && storedIsPdf);
  const fileName = hasNewFile
    ? file.name || "File selected"
    : hasStored
      ? storedDoc.fileName || "Saved on this policy"
      : null;
  const canPreview = !!(imgSrc || hasNewFile || storedUrl);

  const tone = {
    empty: { border: "#cfdbee", bg: "#fcfdff" },
    filled: { border: "#9ec5f0", bg: "#f6faff" },
    saved: { border: "#b7e0c2", bg: "#f5fbf7" },
  }[state];

  const badge =
    state === "filled" ? "SELECTED" : state === "saved" ? "ON FILE" : null;
  const badgeColor = state === "saved" ? "#2e7d32" : "#1565c0";

  // The native drop event carries files on dataTransfer, but every upload
  // handler here reads event.target.files — so hand it the shape it expects
  // rather than teaching all of them a second one.
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    const dropped = e.dataTransfer?.files;
    if (dropped && dropped.length) onSelect?.({ target: { files: dropped } });
  };

  const handlePreview = () => {
    if (imgSrc) {
      setPreviewOpen(true);
      return;
    }
    if (hasNewFile) {
      // A PDF picked just now has no URL yet — make one for the new tab.
      const url = URL.createObjectURL(file);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }
    if (storedUrl) window.open(storedUrl, "_blank", "noopener,noreferrer");
  };

  const buttonSx = {
    textTransform: "none",
    fontSize: "12px",
    minWidth: "auto",
    p: "2px 8px",
  };

  const previewDialog = (
    <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", fontSize: "15px", pr: 1 }}>
        <span style={{ flex: 1 }}>
          {title}
          {fileName ? ` — ${fileName}` : ""}
        </span>
        <IconButton size="small" onClick={() => setPreviewOpen(false)} aria-label="Close preview">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ textAlign: "center", backgroundColor: "#f9fafb" }}>
        {imgSrc && (
          <img src={imgSrc} alt={`${title} preview`} style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }} />
        )}
      </DialogContent>
    </Dialog>
  );

  // The Online Policy form's look (2026-10-23, the owner: "not big big thing ... uniq and simple"): a small tile,
  // several side by side. The same file input, drop target and buttons as the row below — only smaller.
  if (variant === "tile") {
    const done = state !== "empty";
    const tileButtonSx = { ...buttonSx, p: "2px 6px", color: NAVY, fontWeight: 600 };
    const fileInput = (
      <input
        ref={tileInputRef}
        type="file"
        hidden
        disabled={disabled}
        accept="image/jpeg,image/jpg,image/png,application/pdf"
        onChange={onSelect}
      />
    );
    const titleLine = (
      <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, width: "100%" }}>
        {done ? (
          <CheckCircleRoundedIcon sx={{ fontSize: 18, color: "#067647", flexShrink: 0 }} />
        ) : (
          <CloudUploadIcon sx={{ fontSize: 18, color: NAVY, flexShrink: 0 }} />
        )}
        <Typography component="span" sx={{ fontWeight: 600, fontSize: "13px", color: "#344054", whiteSpace: "nowrap" }}>
          {title}
          {required && <span style={{ color: "#B42318" }}> *</span>}
        </Typography>
        {state === "saved" && (
          <Typography
            component="span"
            sx={{
              ml: "auto",
              fontSize: "10.5px",
              fontWeight: 700,
              color: "#067647",
              backgroundColor: "#ECFDF3",
              borderRadius: "999px",
              px: 0.75,
              whiteSpace: "nowrap",
            }}
          >
            On file
          </Typography>
        )}
      </Box>
    );
    const tileSx = {
      width: { xs: "100%", sm: 252 },
      // tiles side by side keep one height; on a phone each is only as tall as it needs
      minHeight: { xs: 0, sm: 100 },
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      justifyContent: "flex-start",
      textAlign: "left",
      gap: 0.25,
      p: 1.25,
      borderRadius: "10px",
      border: `1px ${done && !dragOver ? "solid" : "dashed"} ${
        dragOver ? NAVY : state === "saved" ? "#ABEFC6" : done ? "#C9D7E8" : "#98A2B3"
      }`,
      backgroundColor: dragOver ? "#EEF3F9" : done ? "#fff" : "#FCFCFD",
      // while the card is read nothing can be changed: say so by look too
      opacity: disabled && onSelect ? 0.6 : 1,
      transition: "border-color .15s, background-color .15s, opacity .15s",
    };
    const dropProps = {
      onDragOver: (e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      },
      onDragLeave: () => setDragOver(false),
      onDrop: handleDrop,
    };

    // Empty: the WHOLE tile is the upload button (the owner: "click the upload field then open that, not only the
    // browse button"). A label around the file input, so a click anywhere on it opens the file picker; ButtonBase gives
    // it the keyboard focus and Enter / Space of a button.
    if (!done) {
      return (
        <ButtonBase
          component="label"
          disabled={disabled}
          focusRipple
          // a focused label does not open the picker by itself: Enter / Space do it, as on any button
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !disabled) {
              e.preventDefault();
              if (tileInputRef.current) tileInputRef.current.click();
            }
          }}
          {...dropProps}
          sx={{
            ...tileSx,
            cursor: disabled ? "default" : "pointer",
            "&:hover": disabled ? {} : { borderColor: NAVY, backgroundColor: "#F5F8FC" },
            "&.Mui-focusVisible": { borderColor: NAVY, boxShadow: "0 0 0 3px rgba(30,58,95,0.12)" },
          }}
        >
          {titleLine}
          <Typography component="span" sx={{ pl: "25px", fontSize: "12.5px", color: "#667085" }}>
            <Box component="span" sx={{ fontWeight: 600, color: NAVY }}>
              Click to upload
            </Box>{" "}
            or drop here
          </Typography>
          <Typography component="span" sx={{ pl: "25px", fontSize: "11.5px", color: "#98A2B3" }}>
            JPG, PNG or PDF · max 5MB
          </Typography>
          {fileInput}
        </ButtonBase>
      );
    }

    return (
      <Box {...dropProps} sx={tileSx}>
        {titleLine}

        <Typography
          title={fileName || ""}
          sx={{
            pl: "25px",
            fontSize: "12px",
            color: "#475467",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {isPdf ? <PictureAsPdfIcon sx={{ fontSize: 13, color: "#B42318", mr: 0.5, verticalAlign: "-2px" }} /> : null}
          {fileName}
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.25, mt: "auto", pl: "19px" }}>
          {canPreview && (
            <Button size="small" onClick={handlePreview} sx={tileButtonSx}>
              Preview
            </Button>
          )}
          {!(disabled && !onSelect) && (
            <Button component="label" size="small" disabled={disabled} sx={tileButtonSx}>
              Replace
              {fileInput}
            </Button>
          )}
          {hasNewFile && (
            <Button size="small" disabled={disabled} onClick={onRemove} sx={{ ...tileButtonSx, color: "#B42318" }}>
              Remove
            </Button>
          )}
        </Box>

        {previewDialog}
      </Box>
    );
  }

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        minHeight: 48,
        px: 1.5,
        py: 0.75,
        borderRadius: "8px",
        border: `1px ${state === "empty" || dragOver ? "dashed" : "solid"} ${
          dragOver ? "#1E3A5F" : tone.border
        }`,
        backgroundColor: dragOver ? "#eef3fa" : tone.bg,
        transition: "border-color .15s, background-color .15s",
      }}
    >
      {isPdf ? (
        <PictureAsPdfIcon sx={{ fontSize: 20, color: "#b3261e" }} />
      ) : state === "empty" ? (
        <CloudUploadIcon sx={{ fontSize: 20, color: "#98a2b3" }} />
      ) : (
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 20, color: "#667085" }} />
      )}

      <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#344054", minWidth: 110 }}>
        {title}
        {required && <span style={{ color: "#d32f2f" }}> *</span>}
      </Typography>

      {badge && (
        <Typography
          sx={{
            fontSize: "10px",
            fontWeight: 700,
            color: badgeColor,
            backgroundColor: `${badgeColor}14`,
            border: `1px solid ${badgeColor}33`,
            borderRadius: "999px",
            px: 0.85,
            py: 0.1,
            whiteSpace: "nowrap",
          }}
        >
          {badge}
        </Typography>
      )}

      <Typography
        title={fileName || ""}
        sx={{
          flex: 1,
          minWidth: 120,
          fontSize: "12px",
          color: fileName ? "#667085" : "#98a2b3",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {fileName || "Drop the file here, or click Upload · JPG, PNG or PDF · max 5MB"}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, ml: "auto" }}>
        {canPreview && (
          <Button
            size="small"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={handlePreview}
            sx={buttonSx}
          >
            Preview
          </Button>
        )}

        {!(disabled && !onSelect) && (
          <Button component="label" size="small" disabled={disabled} sx={buttonSx}>
            {state === "empty" ? "Upload" : "Replace"}
            <input
              type="file"
              hidden
              disabled={disabled}
              accept="image/jpeg,image/jpg,image/png,application/pdf"
              onChange={onSelect}
            />
          </Button>
        )}

        {hasNewFile && (
          <Button
            size="small"
            color="error"
            disabled={disabled}
            startIcon={<DeleteIcon sx={{ fontSize: 15 }} />}
            onClick={onRemove}
            sx={buttonSx}
          >
            Remove
          </Button>
        )}
      </Box>

      {previewDialog}
    </Box>
  );
};
const CustomerDetailsStep = ({ values, errors, touched, setFieldValue }) => {
  const [aadharCardFrontPreview, setAadharCardFrontPreview] = useState(null);
  const [aadharCardBackPreview, setAadharCardBackPreview] = useState(null);
  const [aadharCardFront, setAadharCardFront] = useState(null);
  const [aadharCardBack, setAadharCardBack] = useState(null);
  const [financierData, setFinancierData] = useState([]);
  const [selectedFinancier, setSelectedFinancier] = useState(null);

  // New states for single file uploads
  const [aadharCardPreview, setAadharCardPreview] = useState(null);
  const [panCardPreview, setPanCardPreview] = useState(null);
  const [isAadharSeparate, setIsAadharSeparate] = useState(false);
  const [aadharExtracting, setAadharExtracting] = useState(null); // 'both' (a single upload) | 'merged' (separate mode: both sides) | null

  // Raw uploaded Files kept for OCR (previews above only store data URLs)
  const [aadharFrontFile, setAadharFrontFile] = useState(null);
  const [aadharBackFile, setAadharBackFile] = useState(null);

  // Reliance requires BOTH Aadhaar sides uploaded separately — extraction
  // starts only once both are present (never on a single side alone). Every
  // other insurer accepts a single merged scan.
  // Either insurer of a multi-company policy may need its own document format.
  const isReliance = hasCompanyLeg(values, requiresSeparateAadhar);

  // Drives the read-only Aadhaar Number tile below. "Uploaded" counts the
  // merged single file OR either separate side, so the not-clear warning
  // appears as soon as there is something to have read a number FROM — and
  // stays quiet on an empty form, where it would just be noise.
  // Separate mode reads nothing until BOTH sides are in, so one side alone is not "something to read from" (the owner,
  // 2026-10-28: the front alone showed "Could not read the number" before any reading had started). A merged card
  // already on the policy (an edit) still counts.
  const hasAadharUpload = isAadharSeparate
    ? Boolean((values.aadharCardFront && values.aadharCardBack) || values.aadharCard)
    : Boolean(values.aadharCard || values.aadharCardFront || values.aadharCardBack);
  // KSHEMA takes Aadhaar only; the other insurers still offer PAN.
  const showPanCard = hasCompanyLeg(values, hasPanCard);

  // Force the separate front/back upload mode for those insurers
  useEffect(() => {
    if (isReliance && !isAadharSeparate) {
      setIsAadharSeparate(true);
    }
  }, [isReliance]);

  // Going back and switching the vehicle can switch the insurer. Drop a PAN
  // picked under the old one rather than submitting a file whose upload slot
  // is no longer even on screen.
  useEffect(() => {
    if (!showPanCard && values.panCard) {
      setFieldValue("panCard", null);
      setPanCardPreview(null);
    }
  }, [showPanCard]);

  // Apply extracted Aadhaar data into the existing form fields below
  const applyExtractedAadharData = (data) => {
    let filledCount = 0;
    const setIfPresent = (field, value) => {
      if (value !== null && value !== undefined && value !== "") {
        setFieldValue(field, value);
        filledCount++;
      }
    };

    // Customer details (Aadhaar front)
    setIfPresent("fullName", data.fullName);
    setIfPresent("surname", data.surname);
    setIfPresent("dateOfBirth", data.dateOfBirth);
    setIfPresent("gender", data.gender);

    // The Title, from what the card says: a printed salutation (Shri / Smt ...), else the guardian prefix on the back
    // (W/O -> Mrs., D/O -> Ms., S/O -> Mr.), else the gender. The FRONT gives the salutation and gender, the BACK the
    // prefix, and they arrive in any order (or together), so each is remembered and the title is worked out from all
    // that is known. A key that is present but empty ("relation" on a back with no prefix) clears the old one, so a
    // second person's card never inherits the first one's. A title the operator chose by hand is never replaced.
    const salutation = "salutation" in data ? data.salutation || "" : values._aadharSalutation || "";
    const relation = "relation" in data ? data.relation || "" : values._aadharRelation || "";
    if ("salutation" in data) setFieldValue("_aadharSalutation", salutation);
    if ("relation" in data) setFieldValue("_aadharRelation", relation);
    if (!values._titleChosen) {
      const title = suggestTitle({ salutation, relation, gender: data.gender || values.gender });
      if (title) setFieldValue("proposerTitle", title);
    }
    // Only accept a full 12-digit read. A partial number is worse than none:
    // it looks filled in, so nobody re-uploads, and the wrong number reaches
    // the insurer.
    if (isValidAadharNumber(data.aadharNumber)) {
      setIfPresent("aadharNumber", String(data.aadharNumber).replace(/\s/g, ""));
    }

    // Address details (Aadhaar back). floorNo/buildingName/blockName were
    // extracted-but-discarded before, which is why parts of the address looked
    // "missing" even when the AI had read them correctly.
    setIfPresent("fatherName", data.fatherName);
    setIfPresent("flatDoorNo", data.flatDoorNo);
    setIfPresent("floorNo", data.floorNo);
    setIfPresent("buildingName", data.buildingName);
    setIfPresent("blockName", data.blockName);
    setIfPresent("roadStreetLane", data.roadStreetLane);
    setIfPresent("areaAndLocality", data.areaAndLocality);
    setIfPresent("state", data.state);
    setIfPresent("pincode", data.pincode);
    setIfPresent("country", data.country);
    // Only some Aadhaar cards print a mobile number; normalizeAiResult already
    // rejected anything that is not a valid 10-digit Indian number.
    setIfPresent("mobileNumber", data.mobileNumber);

    return filledCount;
  };

  // A read that the operator has overtaken (a side replaced or removed, the checkbox flipped) must not fill the form
  // afterwards: every change of the two sides moves this on, and a read only applies while it is still the latest.
  const readRun = useRef(0);

  // The side without the address (a single upload of the front, say) reads no address at all: say so, rather than
  // leave the operator waiting for boxes that will not fill.
  const reportMissingAddress = (data, side) => {
    if (hasAnyAddress(data)) return;
    ToastError(
      side === "merged"
        ? "No address found on either side of the Aadhaar card — please fill the address."
        : "No address on this picture — upload the side with the address, or fill it in."
    );
  };

  // The number was read, but it fails the Aadhaar check digit even after the backend read it a second time — most
  // likely one digit is misread. The number has no input box here, so the operator is asked to compare it with the
  // card; nothing is blocked (a re-upload of a clearer image reads it again).
  const warnIfAadharNumberDoubtful = (data) => {
    if (!data?.aadharNumberCheckFailed) return;
    ToastError(
      `Please check the Aadhaar number ${data.aadharNumber} against the card — it fails the Aadhaar number check. If it is different, upload a clearer image.`
    );
  };

  // Run OCR on the uploaded Aadhaar file and auto-fill the form fields
  const runAadharExtraction = async (file, side, pdfPassword, isCurrent = () => true) => {
    setAadharExtracting(side);
    try {
      const data = await extractAadharData(file, side, pdfPassword);
      if (!isCurrent()) return;
      const filledCount = applyExtractedAadharData(data);

      // The Aadhaar NUMBER is reported separately from the rest. Every other
      // field can be typed by hand if OCR misses it, but this one is never
      // shown as an input — an unreadable number has no manual fallback, so
      // it has to be called out as "re-upload", not folded into a generic
      // "fill the fields manually".
      const gotNumber = isValidAadharNumber(data?.aadharNumber);

      if (!gotNumber) {
        ToastError(
          "Aadhaar card number is not clear. Please upload a clearer image of the Aadhaar card."
        );
      } else if (filledCount > 0) {
        ToastSuccess(
          `Aadhaar data extracted — ${filledCount} field(s) auto-filled. Please verify them.`
        );
      } else {
        ToastError(
          "Could not read details from the Aadhaar image. Please fill the fields manually."
        );
      }
      if (gotNumber) warnIfAadharNumberDoubtful(data);
      if (filledCount > 0) reportMissingAddress(data, side);
    } catch (error) {
      if (!isCurrent()) return;
      // e-Aadhaar PDFs downloaded from UIDAI are ALWAYS password protected, so
      // this is the normal case for a PDF, not a rare failure. Ask for the
      // password and retry once rather than reporting a dead end.
      if (error instanceof PdfPasswordRequiredError && !pdfPassword) {
        setAadharExtracting(null);
        const entered = window.prompt(
          "This Aadhaar PDF is password protected.\n\nEnter its password " +
          "(usually the first 4 letters of the name in CAPITALS followed by " +
          "the year of birth, e.g. RAME1990):"
        );
        if (entered) {
          return runAadharExtraction(file, side, entered, isCurrent);
        }
        ToastError("Aadhaar PDF needs a password to be read.");
        return;
      }
      console.error("Aadhaar OCR extraction error:", error);
      ToastError(`Aadhaar extraction failed: ${error.message}`);
    } finally {
      if (isCurrent()) setAadharExtracting(null);
    }
  };

  // The fallback for separate mode when the one merged picture could not be made: both sides as two pictures in
  // ONE request (how separate mode always read before 2026-10-28)
  const runBothSidesExtraction = async (frontFile, backFile, pdfPassword, isCurrent = () => true) => {
    setAadharExtracting("merged");
    try {
      // ONE request carrying both images — not one call per side. Cheaper,
      // counted once in the daily extraction log, and the model can
      // cross-check the front against the back instead of returning two
      // halves that may disagree.
      const data = await extractAadharDataFromBothSides(
        frontFile,
        backFile,
        pdfPassword
      );
      if (!isCurrent()) return;
      const filledCount = applyExtractedAadharData(data);
      if (filledCount > 0) {
        ToastSuccess(
          `Aadhaar data extracted — ${filledCount} field(s) auto-filled. Please verify them.`
        );
        reportMissingAddress(data, "merged");
      } else {
        ToastError(
          "Could not read details from the Aadhaar images. Please fill the fields manually."
        );
      }
      if (isValidAadharNumber(data?.aadharNumber)) warnIfAadharNumberDoubtful(data);
    } catch (error) {
      if (!isCurrent()) return;
      if (error instanceof PdfPasswordRequiredError && !pdfPassword) {
        setAadharExtracting(null);
        const entered = window.prompt(
          "This Aadhaar PDF is password protected.\n\nEnter its password " +
          "(usually the first 4 letters of the name in CAPITALS followed by " +
          "the year of birth, e.g. RAME1990):"
        );
        if (entered) {
          return runBothSidesExtraction(frontFile, backFile, entered, isCurrent);
        }
        ToastError("Aadhaar PDF needs a password to be read.");
        return;
      }
      console.error("Aadhaar OCR extraction error:", error);
      ToastError(`Aadhaar extraction failed: ${error.message}`);
    } finally {
      if (isCurrent()) setAadharExtracting(null);
    }
  };

  // Separate-upload mode (Reliance always, or the checkbox for any company): nothing is read until BOTH sides are in.
  // Then the two sides become ONE picture and that picture is read (the owner, 2026-10-28), so the address is found
  // on whichever side prints it. Two pictures are made from the same sides (utils/mergeImages.js): the stacked one is
  // SAVED as the policy's Aadhaar card, as before; the side-by-side one is what the AI reads, because stacked would
  // reach it at well under half the detail. If that copy cannot be made, both sides go up as two pictures instead.
  //
  // Runs on the two PREVIEWS (data URLs), not the Files: a replaced side's File is set before its preview has loaded,
  // and reading then would send the old preview — a wasted, paid read of the wrong picture.
  useEffect(() => {
    const run = ++readRun.current;
    if (!isAadharSeparate || !aadharCardFront || !aadharCardBack) {
      // A side removed or the checkbox flipped while the sides were being read: that read is now stale and will not
      // clear the "reading" state itself, so it is cleared here (only a separate-mode read, never a single upload's).
      setAadharExtracting((now) => (now === "merged" ? null : now));
      return;
    }
    const isCurrent = () => run === readRun.current;

    (async () => {
      // Shown from the start: the tiles are disabled while the pictures are merged, not only while they are read.
      setAadharExtracting("merged");
      try {
        const stacked = await mergeImages(aadharCardFront, aadharCardBack);
        if (!isCurrent()) return;
        setFieldValue("aadharCard", new File([stacked], "Aadhar Card_merged.jpg", { type: "image/jpeg" }));
      } catch (error) {
        console.error("Error merging Aadhar Card images:", error);
        if (isCurrent()) ToastError("Failed to merge Aadhar Card images");
      }

      let readable = null;
      try {
        const blob = await mergeImages(aadharCardFront, aadharCardBack, { layout: "sideBySide", maxSide: AI_MAX_SIDE });
        readable = new File([blob], "Aadhar Card_read.jpg", { type: "image/jpeg" });
      } catch (error) {
        console.error("Could not make the one Aadhaar picture to read, reading the two sides instead:", error);
      }
      if (!isCurrent()) return;

      if (readable) {
        await runAadharExtraction(readable, "merged", undefined, isCurrent);
      } else {
        await runBothSidesExtraction(aadharFrontFile, aadharBackFile, undefined, isCurrent);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAadharSeparate, aadharCardFront, aadharCardBack]);

  // Single file upload handler
  const handleSingleFileUpload = (event, fieldName, previewSetter, showToast = true) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      if (!validTypes.includes(file.type)) {
        ToastError("Please upload only JPG, PNG or PDF files");
        return null;
      }
      if (file.size > 5 * 1024 * 1024) {
        ToastError("File size should not exceed 5MB");
        return null;
      }

      setFieldValue(fieldName, file);

      if (file.type === "application/pdf") {
        previewSetter("pdf");
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          previewSetter(reader.result);
        };
        reader.readAsDataURL(file);
      }

      if (showToast) {
        const fieldLabel = fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        ToastSuccess(`${fieldLabel} uploaded successfully`);
      }
      return file;
    }
    return null;
  };

  // Generic file upload handler.
  //
  // PDFs are converted to a JPEG here, at upload, rather than being handled
  // specially further down: these slots feed an <img> preview AND the canvas
  // merge that builds the combined card image, and neither can load a PDF.
  // Converting once keeps every later step working on an image.
  const handleFileUpload = async (event, fieldName, previewSetter, fileSetter, showToast = true) => {
    const picked = event.target.files[0];
    if (!picked) return null;

    const validTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!validTypes.includes(picked.type) && !isPdfFile(picked)) {
      ToastError("Please upload only JPG, PNG or PDF files");
      return null;
    }

    // Validate file size (max 5MB)
    if (picked.size > 5 * 1024 * 1024) {
      ToastError("File size should not exceed 5MB");
      return null;
    }

    let file = picked;
    if (isPdfFile(picked)) {
      try {
        file = await pdfFileToImageFile(picked);
      } catch (error) {
        // e-Aadhaar PDFs from UIDAI are always password protected, so ask
        // rather than rejecting the file outright.
        if (error instanceof PdfPasswordRequiredError) {
          const entered = window.prompt(
            "This PDF is password protected.\n\nEnter its password (usually " +
            "the first 4 letters of the name in CAPITALS followed by the year " +
            "of birth, e.g. RAME1990):"
          );
          if (!entered) {
            ToastError("PDF needs a password to be read.");
            return null;
          }
          try {
            file = await pdfFileToImageFile(picked, { password: entered });
          } catch (retryError) {
            ToastError(`Could not open the PDF: ${retryError.message}`);
            return null;
          }
        } else {
          ToastError(`Could not read the PDF: ${error.message}`);
          return null;
        }
      }
    }

    // Update Formik state
    setFieldValue(fieldName, file);

    // Create preview for images
    const reader = new FileReader();
    reader.onloadend = () => {
      previewSetter(reader.result);
      fileSetter(reader.result);
    };
    reader.readAsDataURL(file);

    if (showToast) {
      const fieldLabel = fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
      ToastSuccess(`${fieldLabel} uploaded successfully`);
    }
    return file;
  };

  // Handle remove file
  const handleRemoveFile = (fieldName, previewSetter, fileSetter, isFront) => {
    previewSetter(null);
    fileSetter(null);
    setFieldValue(fieldName, null);

    // Also clear the merged file
    if (fieldName.includes("panCard")) {
      setFieldValue("panCard", null);
    } else if (fieldName.includes("aadharCard")) {
      setFieldValue("aadharCard", null);
    }

    const fieldLabel = fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
    ToastSuccess(`${fieldLabel} removed`);
  };

  // Aadhar Card Front upload (auto-extracts name / DOB / gender).
  // Reliance: only store the file — extraction waits until BOTH sides exist
  const handleAadharCardFrontUpload = async (event) => {
    const file = await handleFileUpload(event, "aadharCardFront", setAadharCardFrontPreview, setAadharCardFront, false);
    if (file) {
      // Only store it. These tiles exist ONLY in separate-upload mode, and in
      // that mode extraction must wait for BOTH sides and go up as ONE merged
      // picture — the effect above fires once the other side arrives.
      setAadharFrontFile(file);
      if (!aadharBackFile) {
        ToastSuccess("Aadhaar front uploaded — upload the BACK side to start extraction");
      }
    }
  };

  // Aadhar Card Back upload (auto-extracts address details).
  // Reliance: only store the file — extraction waits until BOTH sides exist
  const handleAadharCardBackUpload = async (event) => {
    const file = await handleFileUpload(event, "aadharCardBack", setAadharCardBackPreview, setAadharCardBack, false);
    if (file) {
      setAadharBackFile(file);
      if (!aadharFrontFile) {
        ToastSuccess("Aadhaar back uploaded — upload the FRONT side to start extraction");
      }
    }
  };

  // Single Aadhar Card upload (auto-extracts both sides' data)
  const handleAadharCardSingleUpload = (event) => {
    const file = handleSingleFileUpload(event, "aadharCard", setAadharCardPreview, false);
    if (file) {
      runAadharExtraction(file, "both");
    }
  };

  // Fetch Financier data from API
  useEffect(() => {
    const fetchFinancierData = async () => {
      try {
        const response = await GetFinancier();
        if (response && response.data) {
          const formattedData = response.data
            .filter((item) => item.isEnabled) // Only active financiers
            .map((item) => ({
              _id: item._id,
              financierType: item.financierType,
              financierName: item.financierName,
              financierAddress: item.financierAddress,
              label: `${item.financierName} (${item.financierType})`,
              value: item._id,
            }));
          setFinancierData(formattedData);
        }
      } catch (error) {
        console.error("Error fetching financier data:", error);
        ToastError("Failed to fetch Financier data");
      }
    };

    fetchFinancierData();
  }, []);

  /**
   * Show the saved financier in the picker when editing a policy.
   *
   * Matches case- and spacing-insensitively: the saved name goes through the
   * form uppercased while the master keeps its own casing, so a strict ===
   * comparison silently failed and the box came up empty — which then wrote
   * the financier back as "" on update.
   *
   * When the saved financier is no longer in the master (disabled or renamed),
   * a stand-in option is built from the policy's own stored values so the field
   * still shows what the policy holds instead of looking unset.
   */
  useEffect(() => {
    if (!values.financierName || selectedFinancier) return;
    if (financierData.length === 0) return;

    const norm = (v) => String(v || "").trim().toLowerCase();
    const match = financierData.find(
      (f) => norm(f.financierName) === norm(values.financierName)
    );

    if (match) {
      setSelectedFinancier(match);
      return;
    }

    setSelectedFinancier({
      _id: `saved-${norm(values.financierName)}`,
      financierType: values.financierType || "",
      financierName: values.financierName,
      financierAddress: values.financierAddress || "",
      label: `${values.financierName}${values.financierType ? ` (${values.financierType})` : ""}`,
      value: values.financierName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financierData, values.financierName, values.financierType, selectedFinancier]);

  return (
    <>
      {/* ── Documents — Aadhaar Auto Fill ───────────────────────────────────
          A self-contained card, deliberately separate from the form fields
          below. It sits on top because everything extracted here flows down
          into those fields. */}
      <FormSection
        title="Documents & Aadhaar Auto Fill"
        description="The customer & address fields below fill in automatically from the Aadhaar card."
        testId="section-documents"
        bodySx={{ pb: 2.5 }}
      >
          <Box>
            {/* Mode notice / toggle */}
            {isReliance ? (
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 1.5 }}>
                <InfoOutlinedIcon sx={{ fontSize: 16, mt: "2px", color: NAVY }} />
                <Typography sx={{ fontSize: "12.5px", color: "#475467" }}>
                  Reliance needs both sides of the Aadhaar card, uploaded separately — reading starts once the front
                  and back are both in.
                </Typography>
              </Box>
            ) : (
              <FormControlLabel
                sx={{ mb: 0.5 }}
                control={
                  <Checkbox
                    checked={isAadharSeparate}
                    onChange={(e) => {
                      setIsAadharSeparate(e.target.checked);
                      // Clear existing values when switching
                      setFieldValue("aadharCard", null);
                      setFieldValue("aadharCardFront", null);
                      setFieldValue("aadharCardBack", null);
                      setAadharCardFront(null);
                      setAadharCardBack(null);
                      setAadharCardFrontPreview(null);
                      setAadharCardBackPreview(null);
                      setAadharCardPreview(null);
                      setAadharFrontFile(null);
                      setAadharBackFile(null);
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: "13px" }}>
                    Upload Aadhaar front and back separately
                  </Typography>
                }
              />
            )}

            {/* Uploads as compact one-line rows, two across on desktop. Each
                row is a drop target with Preview / Replace / Remove buttons —
                the picture itself is behind Preview, so a row stays one line
                high whatever is in it.

                The "both sides uploaded" success banner that used to sit here
                is gone — the Aadhaar number bar below already reports whether
                extraction worked, and saying it twice was the clutter. */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {!isAadharSeparate && (
                <Box sx={DOC_SLOT_SX}>
                  <UploadCard
                    variant="tile"
                    title="Aadhaar Card"
                    required
                    file={values.aadharCard}
                    preview={aadharCardPreview}
                    storedDoc={values.aadharCard}
                    disabled={!!aadharExtracting}
                    onSelect={handleAadharCardSingleUpload}
                    onRemove={() =>
                      handleRemoveFile("aadharCard", setAadharCardPreview, () => { }, null)
                    }
                  />
                </Box>
              )}

              {isAadharSeparate && (
                <>
                  <Box sx={DOC_SLOT_SX}>
                    <UploadCard
                    variant="tile"
                      title="Aadhaar Front"
                      required
                      file={values.aadharCardFront}
                      preview={aadharCardFrontPreview}
                      disabled={!!aadharExtracting}
                      onSelect={handleAadharCardFrontUpload}
                      onRemove={() => {
                        handleRemoveFile("aadharCardFront", setAadharCardFrontPreview, setAadharCardFront, true);
                        setAadharFrontFile(null);
                      }}
                    />
                  </Box>
                  <Box sx={DOC_SLOT_SX}>
                    <UploadCard
                    variant="tile"
                      title="Aadhaar Back"
                      required
                      file={values.aadharCardBack}
                      preview={aadharCardBackPreview}
                      disabled={!!aadharExtracting}
                      onSelect={handleAadharCardBackUpload}
                      onRemove={() => {
                        handleRemoveFile("aadharCardBack", setAadharCardBackPreview, setAadharCardBack, false);
                        setAadharBackFile(null);
                      }}
                    />
                  </Box>
                  {/* In front/back mode what gets SAVED is the merged image,
                      which has no upload cell of its own — so it gets one here
                      purely so an edited policy can still show it. */}
                  {isStoredDocument(values.aadharCard) && (
                    <Box sx={DOC_SLOT_SX}>
                      <UploadCard
                    variant="tile"
                        title="Aadhaar card (merged)"
                        storedDoc={values.aadharCard}
                        disabled
                      />
                    </Box>
                  )}
                </>
              )}

              {/* PAN — every insurer except KSHEMA, which takes Aadhaar only.
                  Optional throughout, hence no asterisk: it is offered because
                  operators do attach it, not because a policy needs it. */}
              {showPanCard && (
                <Box sx={DOC_SLOT_SX}>
                  <UploadCard
                    variant="tile"
                    title="PAN Card"
                    file={values.panCard}
                    preview={panCardPreview}
                    storedDoc={values.panCard}
                    disabled={!!aadharExtracting}
                    onSelect={(e) =>
                      handleSingleFileUpload(e, "panCard", setPanCardPreview)
                    }
                    onRemove={() =>
                      handleRemoveFile("panCard", setPanCardPreview, () => { }, null)
                    }
                  />
                </Box>
              )}
            </Box>

            <AadharNumberStrip
              number={values.aadharNumber}
              extracting={!!aadharExtracting}
              hasUpload={hasAadharUpload}
            />
          </Box>
      </FormSection>

      {/* Customer Details Section */}
      <FormSection title="Customer details" description="The policy holder, as on the Aadhaar card." testId="section-customer">
          <Grid container columnSpacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Full Name *
              </Typography>
              <Field
                name="fullName"
                placeholder="Full Name"
                className="textField w-100"
                onChange={upperCaseChange(setFieldValue, "fullName")}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.fullName && touched.fullName ? (
                  <div>{errors.fullName}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Surname *
              </Typography>
              <Field
                name="surname"
                placeholder="Surname"
                className="textField w-100"
                onChange={upperCaseChange(setFieldValue, "surname")}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.surname && touched.surname ? (
                  <div>{errors.surname}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Date of Birth *
              </Typography>
              <DatePicker
                name="dateOfBirth"
                className="Date_Picker w-100"
                value={values.dateOfBirth ? dayjs(values.dateOfBirth) : null}
                onChange={(e) => setFieldValue("dateOfBirth", e?.toDate())}
                placeholder="Date of Birth"
                format="DD/MM/YYYY"
                getPopupContainer={(trigger) => trigger.parentElement}
                changeOnBlur={false}
                popupStyle={{ zIndex: 9999 }}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.dateOfBirth && touched.dateOfBirth ? (
                  <div>{errors.dateOfBirth}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Gender *
              </Typography>
              <Autocomplete
                name="gender"
                className="AutoComplete_InputBox"
                disablePortal
                value={
                  genderOptions.find(
                    (o) => o.value.toLowerCase() === String(values.gender || "").toLowerCase()
                  ) || null
                }
                options={genderOptions}
                onChange={(e, v) => {
                  const gender = v?.value || "";
                  setFieldValue("gender", gender);
                  // The Title follows the Gender until the operator has chosen one themselves.
                  setFieldValue(
                    "proposerTitle",
                    titleAfterGenderChange({
                      gender,
                      currentTitle: values.proposerTitle,
                      titleChosen: values._titleChosen,
                      salutation: values._aadharSalutation,
                      relation: values._aadharRelation,
                    })
                  );
                }}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Gender" />
                )}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.gender && touched.gender ? (
                  <div>{errors.gender}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Father's Name *
              </Typography>
              <Field
                name="fatherName"
                placeholder="Father's Name"
                className="textField w-100"
                onChange={upperCaseChange(setFieldValue, "fatherName")}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.fatherName && touched.fatherName ? (
                  <div>{errors.fatherName}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                {/* Reliance's proposer form requires it (validationSchemas.js); the other insurers take the Gender's. */}
                {isReliance ? "Title *" : "Title"}
              </Typography>
              <Autocomplete
                name="proposerTitle"
                className="AutoComplete_InputBox"
                disablePortal
                disableClearable
                value={titleOptions.find((o) => o.value === values.proposerTitle) || null}
                options={titleOptions}
                onChange={(e, v) => {
                  setFieldValue("proposerTitle", v?.value || "");
                  // A title picked by hand is never overridden by a later Gender change.
                  setFieldValue("_titleChosen", true);
                }}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Title (from Gender)" />
                )}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.proposerTitle && touched.proposerTitle ? (
                  <div>{errors.proposerTitle}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>




          </Grid>
      </FormSection>

      {/* Address Details Section */}
      <FormSection title="Address details" description="Where the policy holder lives, and how to reach them." testId="section-address">
          <Grid container columnSpacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Flat/Room No *
              </Typography>
              <Field
                name="flatDoorNo"
                placeholder="Flat/Room No"
                className="textField w-100"
                onChange={upperCaseChange(setFieldValue, "flatDoorNo")}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.flatDoorNo && touched.flatDoorNo ? (
                  <div>{errors.flatDoorNo}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Floor No *
              </Typography>
              <Field
                name="floorNo"
                placeholder="Floor No"
                className="textField w-100"
                onChange={upperCaseChange(setFieldValue, "floorNo")}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.floorNo && touched.floorNo ? (
                  <div>{errors.floorNo}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Name of Premises *
              </Typography>
              <Field
                name="buildingName"
                placeholder="Name of Premises"
                className="textField w-100"
                onChange={upperCaseChange(setFieldValue, "buildingName")}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.buildingName && touched.buildingName ? (
                  <div>{errors.buildingName}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Block Name/No *
              </Typography>
              <Field
                name="blockName"
                placeholder="Block Name/No"
                className="textField w-100"
                onChange={upperCaseChange(setFieldValue, "blockName")}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.blockName && touched.blockName ? (
                  <div>{errors.blockName}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Road/Street/Lane *
              </Typography>
              <Field
                name="roadStreetLane"
                placeholder="Road/Street/Lane"
                className="textField w-100"
                onChange={upperCaseChange(setFieldValue, "roadStreetLane")}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.roadStreetLane && touched.roadStreetLane ? (
                  <div>{errors.roadStreetLane}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Area and Locality *
              </Typography>
              <Field
                name="areaAndLocality"
                placeholder="Area and Locality"
                className="textField w-100"
                onChange={upperCaseChange(setFieldValue, "areaAndLocality")}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.areaAndLocality && touched.areaAndLocality ? (
                  <div>{errors.areaAndLocality}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                State/Union Territory *
              </Typography>
              <Autocomplete
                name="state"
                className="AutoComplete_InputBox"
                disablePortal
                value={
                  stateOptions.find(
                    (o) => o.value.toLowerCase() === String(values.state || "").toLowerCase()
                  ) || null
                }
                options={stateOptions}
                onChange={(e, v) => {
                  setFieldValue("state", v?.value || "");
                }}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                renderInput={(params) => (
                  <TextField {...params} placeholder="State/Union Territory" />
                )}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.state && touched.state ? (
                  <div>{errors.state}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Country *
              </Typography>
              <Field
                name="country"
                placeholder="Country"
                className="textField w-100"
                onChange={upperCaseChange(setFieldValue, "country")}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.country && touched.country ? (
                  <div>{errors.country}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Pincode *
              </Typography>
              <Field
                name="pincode"
                placeholder="Pincode"
                className="textField w-100"
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.pincode && touched.pincode ? (
                  <div>{errors.pincode}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Email *
              </Typography>
              <Field
                name="email"
                placeholder="Email"
                type="email"
                className="textField w-100"
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.email && touched.email ? (
                  <div>{errors.email}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Mobile Number *
              </Typography>
              <Field
                name="mobileNumber"
                placeholder="Mobile Number"
                className="textField w-100"
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.mobileNumber && touched.mobileNumber ? (
                  <div>{errors.mobileNumber}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>
          </Grid>
      </FormSection>

      {/* Vehicle Details Section */}
      <FormSection title="Vehicle details" description="Engine and chassis numbers, and the financier if the vehicle is on loan." testId="section-vehicle">
          <Grid container columnSpacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Engine Number *
              </Typography>
              <Field
                name="engineNumber"
                placeholder="Engine Number"
                className="textField w-100"
                onChange={upperCaseChange(setFieldValue, "engineNumber")}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.engineNumber && touched.engineNumber ? (
                  <div>{errors.engineNumber}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                Chassis Number *
              </Typography>
              <Field
                name="chassisNumber"
                placeholder="Chassis Number"
                className="textField w-100"
                onChange={upperCaseChange(setFieldValue, "chassisNumber")}
              />
              <div className="errorMessage mb-2 mt-2">
                {errors.chassisNumber && touched.chassisNumber ? (
                  <div>{errors.chassisNumber}</div>
                ) : (
                  " "
                )}
              </div>
            </Grid>

            <Grid item xs={12} sm={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.hasFinancier}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFieldValue("hasFinancier", isChecked);
                      if (!isChecked) {
                        // Clear financier fields when checkbox is unchecked
                        setFieldValue("financierType", "");
                        setFieldValue("financierName", "");
                        setFieldValue("financierAddress", "");
                        setSelectedFinancier(null);
                      }
                    }}
                  />
                }
                label="Financier"
              />
            </Grid>
            {/* KSHEMA only: its portal asks just the financier's NAME and Additional Details, so that is all this asks.
                The details are stored in financierAddress. No master dropdown and no Type (they are other insurers'). */}
            {values.hasFinancier && isKshemaOnlyPolicy(values) && (
              <Grid item xs={12}>
                <Grid container columnSpacing={3} data-testid="kshema-financier">
                  <Grid item xs={12}>
                    <Typography sx={SUBHEAD_SX}>
                      Financier Details
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ mb: 1, fontWeight: "bold" }}>Financier Name *</Typography>
                    <Field
                      name="financierName"
                      placeholder="Financier Name"
                      className="textField w-100"
                      onChange={upperCaseChange(setFieldValue, "financierName")}
                    />
                    <Typography sx={{ fontSize: 12, color: "#667085", mt: 0.5 }}>
                      KSHEMA searches its financier list with this name and the first match is chosen — type at least {KSHEMA_FINANCIER_NAME_MIN_LETTERS} letters, enough to find the right one.
                    </Typography>
                    <div className="errorMessage mb-2 mt-2">
                      {errors.financierName && touched.financierName ? <div>{errors.financierName}</div> : " "}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ mb: 1, fontWeight: "bold" }}>Additional Details</Typography>
                    <Field
                      name="financierAddress"
                      placeholder="Additional Details"
                      className="textField w-100"
                      onChange={upperCaseChange(setFieldValue, "financierAddress")}
                    />
                    <div className="errorMessage mb-2 mt-2">
                      {errors.financierAddress && touched.financierAddress ? <div>{errors.financierAddress}</div> : " "}
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {values.hasFinancier && !isKshemaOnlyPolicy(values) && (
              <>
                <Grid item xs={12}>
                  <Grid container columnSpacing={3}>
                    <Grid item xs={12}>
                      <Typography sx={SUBHEAD_SX}>
                        Financier Details
                      </Typography>
                    </Grid>

                    {/* Its own row: pick a financier here FIRST, then the
                        fields below fill in and become editable — keeping it
                        alone makes that cause-and-effect obvious instead of
                        burying the dropdown among the fields it fills. */}
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                        Select Financier *
                      </Typography>
                      <Autocomplete
                        name="financierName"
                        className="AutoComplete_InputBox"
                        disablePortal
                        value={selectedFinancier}
                        options={financierData}
                        onChange={(e, v) => {
                          setSelectedFinancier(v);
                          if (v) {
                            setFieldValue("financierType", v.financierType || "");
                            setFieldValue("financierName", v.financierName || "");
                            setFieldValue("financierAddress", v.financierAddress || "");
                          } else {
                            setFieldValue("financierType", "");
                            setFieldValue("financierName", "");
                            setFieldValue("financierAddress", "");
                          }
                        }}
                        getOptionLabel={(option) => {
                          if (!option) return "";
                          return option.label || "";
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option?._id === value?._id
                        }
                        renderOption={(props, option) => (
                          <li {...props} key={option._id}>
                            <div>
                              <div style={{ fontWeight: "bold" }}>
                                {option.financierName}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                Type: {option.financierType}
                              </div>
                              {option.financierAddress && (
                                <div style={{ fontSize: "12px", color: "#666" }}>
                                  Address: {option.financierAddress}
                                </div>
                              )}
                            </div>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select Financier" />
                        )}
                      />
                      <Typography sx={{ fontSize: 12, color: "#667085", mt: 0.5 }}>
                        Fills in the details below — you can edit them after.
                      </Typography>
                    </Grid>

                    {/* A row of its own too, below the dropdown, so the group
                        it fills reads as one unit. */}
                    <Grid item xs={12}>
                      <Grid container columnSpacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                            Financier Type
                          </Typography>
                          <Field
                            name="financierType"
                            placeholder="Financier Type"
                            className="textField w-100"
                          />
                          <div className="errorMessage mb-2 mt-2">
                            {errors.financierType && touched.financierType ? (
                              <div>{errors.financierType}</div>
                            ) : (
                              " "
                            )}
                          </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                            Financier Name *
                          </Typography>
                          <Field
                            name="financierName"
                            placeholder="Financier Name"
                            className="textField w-100"
                          />
                          <div className="errorMessage mb-2 mt-2">
                            {errors.financierName && touched.financierName ? (
                              <div>{errors.financierName}</div>
                            ) : (
                              " "
                            )}
                          </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography sx={{ mb: 1, fontWeight: "bold" }}>
                            Financier Address
                          </Typography>
                          <Field
                            name="financierAddress"
                            placeholder="Financier Address"
                            className="textField w-100"
                          />
                          <div className="errorMessage mb-2 mt-2">
                            {errors.financierAddress && touched.financierAddress ? (
                              <div>{errors.financierAddress}</div>
                            ) : (
                              " "
                            )}
                          </div>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
      </FormSection>

      {/* The customer's existing TP policy: OD only (required); not on the form for Multiple company — ExistingTpPolicySection */}
      <ExistingTpPolicySection values={values} errors={errors} touched={touched} setFieldValue={setFieldValue} />
    </>
  );
};

// Reused by Edit -> KYC files (KycFilesDialog.js): the same upload card and Aadhaar front + back merge.
export { UploadCard, mergeImages };

export default CustomerDetailsStep;
